import React, { useEffect } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import Button, { LinkButton } from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { EDIT_PAGE_V2, EDIT_BLOG_V2 } from '@confluence/named-routes/entry-points/routes';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import { getStatusFromResponse } from './util.js';
import { useExperimentalMigratePagesMutation } from './useExperimentalMigratePagesMutation';

const i18n = defineMessages({
	elligibleMigrationModalTitle: {
		id: 'fabric-page-optin-migration.elligible-migration-modal.title',
		defaultMessage: 'Time to convert your page',
		description: 'Title for migration modal',
	},
	elligibleMigrationModalDescription: {
		id: 'fabric-page-optin-migration.elligible-migration-modal.description',
		defaultMessage:
			'This page is compatible with the cloud editor. The legacy editor will be phased out soon, find out more in the <link>announcement</link>.',
		description: 'Description for migration modal',
	},
	elligibleMigrationModalUpdateButton: {
		id: 'fabric-page-optin-migration.elligible-migration-modal.update-button',
		defaultMessage: 'Convert page',
		description: 'Update button for migration modal',
	},
	elligibleMigrationModalCancelButton: {
		id: 'fabric-page-optin-migration.elligible-migration-modal.cancel-button',
		defaultMessage: 'Not now',
		description: 'Cancel button for migration modal',
	},
	elligibleMigrationModalUnsupported: {
		id: 'fabric-page-optin-migration.elligible-migration-modal.unsupported',
		defaultMessage: 'This page cannot be converted',
		description: 'Error for converting page',
	},
});

const popupContainerStyle = xcss({
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	font: token('font.body.small'),
});

const topMarginStyle = xcss({
	marginTop: 'space.200',
});

const buttonContainerStyle = xcss({
	display: 'flex',
	justifyContent: 'flex-end',
	gap: 'space.200',
});

const widthStyle = xcss({
	width: '297px',
});

type EditPopupEligibleMigrationProps = {
	onClickEdit: (e: React.MouseEvent<HTMLAnchorElement>) => void;
	contentId: string;
	href?: string;
	contentHash: string;
	contentType?: string;
	spaceKey?: string;
};

export const EditPopupEligibleMigration = ({
	onClickEdit,
	contentId,
	href,
	contentHash,
	contentType,
	spaceKey,
}: EditPopupEligibleMigrationProps) => {
	const [popupMessage, setPopupMessage] = React.useState({
		...i18n.elligibleMigrationModalDescription,
	});
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [migratePages] = useExperimentalMigratePagesMutation();

	const { push } = useRouteActions();

	const { isLoggedIn } = useSessionData();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'eligibleMigrationModal',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const onClickConvert = React.useCallback(
		async (e) => {
			e.preventDefault();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'eligibleMigrationModalConvertButton',
					source: 'editPopupEligibleMigration',
				},
			}).fire();
			const response = await migratePages({
				variables: {
					contentId,
					contentHash,
				},
			});
			const status = getStatusFromResponse(response);

			if (status === '200' || status === '204') {
				const redirectRoute =
					contentType === 'page'
						? EDIT_PAGE_V2.toUrl({
								contentType: 'pages',
								spaceKey,
								contentId,
							})
						: EDIT_BLOG_V2.toUrl({
								contentType: 'blog',
								spaceKey,
								contentId,
							});
				// perform redirect
				push(redirectRoute);
				confluenceLocalStorageInstance.setItem(keys.PAGE_JUST_CONVERTED, contentId);
			} else {
				setPopupMessage(i18n.elligibleMigrationModalUnsupported);
				if (response && 'errors' in response && response.errors) {
					markErrorAsHandled(response.errors);
				}
			}
		},
		[createAnalyticsEvent, migratePages, contentId, contentHash, contentType, spaceKey, push],
	);
	if (!isLoggedIn || !href) {
		return null;
	}

	return (
		<Box xcss={popupContainerStyle}>
			<Box xcss={widthStyle}>
				<FormattedMessage tagName="h3" {...i18n.elligibleMigrationModalTitle} />
				<Box xcss={topMarginStyle} role="status" aria-live="polite" aria-atomic="true">
					<FormattedMessage
						{...popupMessage}
						tagName="p"
						values={{
							b: (chunks) => <b>{chunks}</b>,
							link: (chunks) => (
								<Anchor
									href="https://community.atlassian.com/forums/Confluence-articles/The-Legacy-Editor-is-being-deprecated-in-Confluence-Cloud-Here-s/ba-p/3046832"
									target="_blank"
									rel="noopener noreferrer"
								>
									{chunks}
								</Anchor>
							),
						}}
					/>
				</Box>
				<Box xcss={topMarginStyle}>
					<Box xcss={buttonContainerStyle}>
						<LinkButton appearance="subtle" href={href} onClick={onClickEdit}>
							<FormattedMessage {...i18n.elligibleMigrationModalCancelButton} />
						</LinkButton>
						<Button appearance="primary" onClick={onClickConvert}>
							<FormattedMessage {...i18n.elligibleMigrationModalUpdateButton} />
						</Button>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
